// components/hotel/HotelDetailsPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getHotelDetails } from '../../../../api/hotel/hotelApi';
import { getDashboardStats } from '../../../../api/user/property/property';
import Loader from '../../../common/Loader.jsx';
import Error from '../../../common/Error.jsx';
import SEO from '../../../seo/SEO';

import RoomList from './RoomList';
import HotelGallery from './HotelGallery';
import HotelAmenities from './HotelAmenities';
import BookingForm from './BookingForm';
import styles from './css/HotelDetailsPage.module.css';
import UserProtectedRoute from '../../../protected-routes/user-protected-route.jsx';

const HotelDetailsPage = () => {
    const { id } = useParams();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [hotel, setHotel] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedRoom, setSelectedRoom] = useState(null);
    const [isVerifyingSession, setIsVerifyingSession] = useState(false);
    const [selectedRoomId, setSelectedRoomId] = useState(null);
    const [isFavorited, setIsFavorited] = useState(false);
    const roomsRef = useRef(null);

    useEffect(() => {
        const fetchHotelDetails = async () => {
            try {
                const response = await getHotelDetails(id);
                if (response.success) {
                    setHotel(response.data);
                } else {
                    setError('Hotel not found');
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchHotelDetails();
    }, [id]);

    const scrollToRooms = () => {
        roomsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const handleSelectRoom = async (room) => {
        setSelectedRoomId(room._id);
        setIsVerifyingSession(true);
        try {
            const response = await getDashboardStats();
            if (response.success) {
                setSelectedRoom(room);
            } else {
                navigate('/login', { state: { from: window.location.pathname } });
            }
        } catch (error) {
            navigate('/login', { state: { from: window.location.pathname } });
        } finally {
            setIsVerifyingSession(false);
        }
    };

    if (loading) return <Loader />;
    if (error) return <Error errMessage={error} />;
    if (!hotel) return <div className={styles.notFound}>Hotel not found</div>;

    const imageCount = hotel.images?.length || 0;
    const rating = hotel.ratings?.average || 0;
    const ratingCount = hotel.ratings?.count || 0;
    const minPrice = hotel.rooms?.length
        ? Math.min(...hotel.rooms.map((r) => r.pricePerNight || 0))
        : 0;

    const shareHotel = async () => {
        try {
            await navigator.clipboard.writeText(window.location.href);
        } catch (e) {
            /* clipboard indisponible — on ignore */
        }
    };

    return (
        <>
            <SEO
                title={hotel.hotelName}
                description={hotel.description || `Book your stay at ${hotel.hotelName} in ${hotel.city}. ${hotel.mainCategory} with excellent amenities and comfortable rooms.`}
                keywords={`hotel, ${hotel.mainCategory}, ${hotel.city}, accommodation, booking, ${hotel.amenities?.join(', ') || ''}`}
                ogImage={hotel.images?.[0]}
                ogType="hotel"
                structuredData={{
                    '@context': 'https://schema.org',
                    '@type': 'Hotel',
                    name: hotel.hotelName,
                    description: hotel.description,
                    address: {
                        '@type': 'PostalAddress',
                        streetAddress: hotel.address,
                        addressLocality: hotel.city,
                        addressCountry: 'CM'
                    },
                    starRating: hotel.ratings?.average,
                    priceRange: hotel.priceRange ? `$${hotel.priceRange.min} - $${hotel.priceRange.max}` : undefined,
                    amenityFeature: hotel.amenities?.map(amenity => ({
                        '@type': 'LocationFeatureSpecification',
                        name: amenity
                    })) || []
                }}
            />
            <div className={styles.hotelDetailsContainer}>
                {/* Breadcrumb */}
                <nav className={styles.breadcrumb} aria-label="Breadcrumb">
                    <Link to="/" className={styles.breadcrumbHome}>
                        <i className="fa fa-home" aria-hidden="true" /> {t('hotel_details.home', 'Home')}
                    </Link>
                    <span className={styles.breadcrumbSep}>›</span>
                    <Link to="/hotels">{t('hotel_details.hotels', 'Hotels')}</Link>
                    <span className={styles.breadcrumbSep}>›</span>
                    <span className={styles.breadcrumbCurrent}>{hotel.hotelName}</span>
                </nav>

                {/* Header minimal */}
                <div className={styles.hotelHeader}>
                    <div className={styles.headerTop}>
                        <div>
                            <h1 className={styles.hotelName}>{hotel.hotelName}</h1>
                            {/* Breadcrumb */}
                            <div className={styles.hotelMetaRow}>
                                {hotel.starRating > 0 && (
                                    <span className={styles.starRating}>
                                        {'★'.repeat(hotel.starRating)}
                                    </span>
                                )}
                                <span className={styles.hotelLocation}>
                                    <span className={styles.locationIcon}><i className="fa fa-map-marker" /></span>
                                    <a
                                        href={`https://maps.google.com/?q=${encodeURIComponent(`${hotel.address}, ${hotel.city}`)}`}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        {hotel.address}, {hotel.city}
                                    </a>
                                </span>
                                {ratingCount > 0 && (
                                    <span className={styles.ratingCount}>({ratingCount} {t('hotel_details.reviews', 'reviews')})</span>
                                )}
                            </div>
                        </div>

                        <div className={styles.headerActions}>
                            {rating > 0 && (
                                <div className={styles.ratingBadge}>
                                    <span className={styles.ratingBadgeValue}>{rating.toFixed(1)}</span>
                                    <span className={styles.ratingBadgeLabel}>/ 5</span>
                                </div>
                            )}
                            <button
                                className={`${styles.iconBtn} ${isFavorited ? styles.iconBtnActive : ''}`}
                                onClick={() => setIsFavorited((f) => !f)}
                                aria-label="Save to favorites"
                            >
                                <i className={`fa ${isFavorited ? 'fa-heart' : 'fa-heart-o'}`} />
                            </button>
                            <button
                                className={styles.iconBtn}
                                onClick={shareHotel}
                                aria-label="Share hotel"
                            >
                                <i className="fa fa-share-alt" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Ancres rapides (mobile) */}
                <div className={styles.sectionAnchors}>                            <button className={styles.anchorBtn} onClick={() => roomsRef.current?.scrollIntoView({ behavior: 'smooth' })}>
                        {t('hotel_details.rooms', 'Rooms')}
                    </button>
                    <button className={styles.anchorBtn} onClick={() => document.getElementById('hotel-amenities')?.scrollIntoView({ behavior: 'smooth' })}>
                        {t('hotel_details.amenities', 'Amenities')}
                    </button>
                    <button className={styles.anchorBtn} onClick={() => document.getElementById('hotel-about')?.scrollIntoView({ behavior: 'smooth' })}>
                        {t('hotel_details.about', 'About')}
                    </button>
                </div>

                {/* Galerie immersive */}
                <HotelGallery images={hotel.images} />

                <div className={styles.hotelContent}>
                    <div className={styles.hotelMain}>
                        <div id="hotel-about" className={`${styles.section}`}>
                            <h2 className={styles.sectionTitle}>
                                <span className={styles.sectionTitleIcon}><i className="fa fa-info-circle" /></span>
                                {t('hotel_details.about_hotel', 'About this hotel')}
                            </h2>
                            <p className={styles.descriptionText}>{hotel.description}</p>
                        </div>

                        <div ref={roomsRef} className={`${styles.section}`}>
                            <div className={styles.sectionHeader}>
                                <h2 className={styles.sectionTitle}>
                                    <span className={styles.sectionTitleIcon}><i className="fa fa-bed" /></span>
                                    {t('hotel_details.rooms_suites', 'Rooms & Suites')}
                                </h2>
                                <span className={styles.roomCount}>{hotel.rooms?.length || 0} {t('hotel_details.room_types', 'room types')}</span>
                            </div>
                            <RoomList
                                rooms={hotel.rooms}
                                onSelectRoom={handleSelectRoom}
                                isVerifyingSession={isVerifyingSession}
                                selectedRoomId={selectedRoomId}
                            />
                        </div>

                        <div id="hotel-amenities">
                            <HotelAmenities amenities={hotel.amenities} />
                        </div>
                    </div>

                    {/* Sidebar sticky */}
                    <div className={styles.hotelSidebar}>
                        <div className={styles.summaryCard}>
                            <div className={styles.summaryPrice}>
                                <span className={styles.summaryPriceAmount}>
                                    {minPrice > 0 ? `XAF ${minPrice.toLocaleString()}` : t('hotel_details.on_request', 'On request')}
                                </span>
                                {minPrice > 0 && <span className={styles.summaryPriceUnit}>/ {t('hotel_details.night', 'night')}</span>}
                            </div>
                            <div className={styles.summaryNights}>
                                {hotel.rooms?.length || 0} {t('hotel_details.room_types_available', 'room types available')}
                            </div>

                            <div className={styles.summaryDivider} />

                            <div className={styles.summaryRow}>
                                <span className={styles.summaryRowLabel}>{t('hotel_details.check_in', 'Check-in')}</span>
                                <span className={styles.summaryRowValue}>{t('hotel_details.from_12', 'From 12:00')}</span>
                            </div>
                            <div className={styles.summaryRow}>
                                <span className={styles.summaryRowLabel}>{t('hotel_details.check_out', 'Check-out')}</span>
                                <span className={styles.summaryRowValue}>{t('hotel_details.until_12', 'Until 12:00')}</span>
                            </div>

                            <button className={styles.summaryCta} onClick={scrollToRooms}>
                                <i className="fa fa-calendar-check-o" />
                                {selectedRoom ? t('hotel_details.update_booking', 'Update booking') : t('hotel_details.check_availability', 'Check availability')}
                            </button>

                            <div className={styles.summaryReassure}>
                                <i className="fa fa-lock" />
                                {t('hotel_details.secure_booking', 'Secure booking — no charge yet')}
                            </div>
                        </div>

                        {selectedRoom ? (
                            <BookingForm
                                hotel={hotel}
                                room={selectedRoom}
                                priceRange={hotel.priceRange}
                                onClose={() => setSelectedRoom(null)}
                            />
                        ) : (
                            <div className={styles.miniInfoCard}>
                                <span className={styles.miniInfoIcon}><i className="fa fa-shield" /></span>
                                <div>
                                    <div className={styles.miniInfoLabel}>{t('hotel_details.verified_property', 'Verified property')}</div>
                                    <div className={styles.miniInfoValue}>{t('hotel_details.quality_checked', 'Quality checked by Spotfli')}</div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Barre CTA mobile sticky */}
            {minPrice > 0 && (
                <div className={styles.mobileCtaBar}>
                    <div className={styles.mobileCtaPrice}>
                        <span className={styles.mobileCtaAmount}>XAF {minPrice.toLocaleString()}</span>
                        <span className={styles.mobileCtaUnit}>/ {t('hotel_details.night', 'night')}</span>
                    </div>
                    <button className={styles.mobileCtaBtn} onClick={scrollToRooms}>
                        <i className="fa fa-calendar-check-o" /> {t('hotel_details.book_now', 'Book now')}
                    </button>
                </div>
            )}
        </>
    );
};

export default HotelDetailsPage;
